import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Users, MapPin, ClipboardList, LogOut, Phone, UserPlus,
  Activity, TrendingUp, AlertCircle, Clock, Shield,
  CheckCircle, Navigation, Bell, Settings, Plus, Eye, Menu, Route,
  Edit, Trash2, UserCheck, RefreshCw, MessageCircle, Send, X, Lock, Key,
  BookUser, MapPinned, PhoneCall
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import CreateStaffModal from './CreateStaffModal';
import CreateAdminModal from './CreateAdminModal';
import TaskAssignmentModal from './TaskAssignmentModal';
import EnhancedLiveLocationMap from './EnhancedLiveLocationMap';
import EnhancedActivityMaps from './EnhancedActivityMaps';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ImprovedAdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [staff, setStaff] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview');
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showLiveMap, setShowLiveMap] = useState(false);
  const [showActivityMap, setShowActivityMap] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
  const [selectedTaskForDetail, setSelectedTaskForDetail] = useState(null);
  const [taskComments, setTaskComments] = useState([]);
  const [newAdminComment, setNewAdminComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [sendingComment, setSendingComment] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [verifyingTask, setVerifyingTask] = useState(false);
  const [inFieldTasks, setInFieldTasks] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // For Staff Directory
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ show: false, user: null, type: null });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('field_staff_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const userData = localStorage.getItem('field_staff_user');
      const parsedUser = userData ? JSON.parse(userData) : null;

      const [statsRes, staffRes, tasksRes, locationsRes, adminsRes] = await Promise.all([
        axios.get(`${API_URL}/api/field-staff/super-admin/reports/dashboard-stats`, config),
        axios.get(`${API_URL}/api/field-staff/admin/staff`, config),
        axios.get(`${API_URL}/api/field-staff/admin/tasks?limit=50`, config),
        axios.get(`${API_URL}/api/field-staff/admin/staff-locations`, config),
        parsedUser?.role === 'super_admin' ? axios.get(`${API_URL}/api/field-staff/super-admin/admins`, config) : Promise.resolve({ data: [] })
      ]);

      setStats(statsRes.data);
      setStaff(staffRes.data);
      setTasks(tasksRes.data);
      setLocations(locationsRes.data);
      setAdmins(adminsRes.data);
      
      // Filter in-field tasks (status = in_progress)
      const inField = tasksRes.data.filter(t => t.status === 'in_progress');
      setInFieldTasks(inField);
      
      // Combine all users for Staff Directory
      const allStaffAndAdmins = [
        ...staffRes.data.map(s => ({ ...s, userType: 'staff' })),
        ...adminsRes.data.map(a => ({ ...a, userType: a.role === 'super_admin' ? 'super_admin' : 'admin' }))
      ];
      setAllUsers(allStaffAndAdmins);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('field_staff_token');
        localStorage.removeItem('field_staff_user');
        navigate('/monitoring-dashboard/field-staff');
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('field_staff_token');
    const userData = localStorage.getItem('field_staff_user');
    
    if (!token || !userData) {
      navigate('/monitoring-dashboard/field-staff');
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'super_admin' && parsedUser.role !== 'admin') {
      navigate('/monitoring-dashboard/field-staff');
      return;
    }

    setUser(parsedUser);
    fetchDashboardData();

    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('field_staff_token');
    localStorage.removeItem('field_staff_user');
    navigate('/monitoring-dashboard/field-staff');
  };

  const handleCall = (phoneNumber) => {
    if (phoneNumber) {
      window.open(`tel:${phoneNumber}`, '_self');
    }
  };

  // Delete Staff or Admin
  const handleDeleteUser = async () => {
    if (!deleteConfirmModal.user) return;
    
    try {
      const token = localStorage.getItem('field_staff_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const { user: userToDelete, type } = deleteConfirmModal;
      
      if (type === 'staff') {
        await axios.delete(`${API_URL}/api/field-staff/admin/staff/${userToDelete.id}`, config);
        toast.success(`Staff member "${userToDelete.name}" deleted successfully`);
      } else if (type === 'admin') {
        await axios.delete(`${API_URL}/api/field-staff/super-admin/admins/${userToDelete.id}`, config);
        toast.success(`Admin "${userToDelete.name}" deleted successfully`);
      }
      
      setDeleteConfirmModal({ show: false, user: null, type: null });
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openDeleteConfirm = (userObj, type) => {
    setDeleteConfirmModal({ show: true, user: userObj, type });
  };

  const handleEditTask = (task) => {
    setEditingTask({...task});
    setShowEditTaskModal(true);
  };

  // Delete Task (Super Admin only)
  const [deleteTaskModal, setDeleteTaskModal] = useState({ show: false, task: null });
  const [deletingTask, setDeletingTask] = useState(false);

  const handleDeleteTask = async () => {
    if (!deleteTaskModal.task) return;
    
    try {
      setDeletingTask(true);
      const token = localStorage.getItem('field_staff_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.delete(`${API_URL}/api/field-staff/admin/tasks/${deleteTaskModal.task.id}`, config);
      
      toast.success(`Task "${deleteTaskModal.task.title}" deleted successfully`);
      setDeleteTaskModal({ show: false, task: null });
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    } finally {
      setDeletingTask(false);
    }
  };

  const handleUpdateTask = async () => {
    if (!editingTask) return;
    
    try {
      const token = localStorage.getItem('field_staff_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.put(`${API_URL}/api/field-staff/admin/tasks/${editingTask.id}`, {
        title: editingTask.title,
        description: editingTask.description,
        priority: editingTask.priority,
        status: editingTask.status,
        assigned_to: editingTask.assigned_to,
        due_date: editingTask.due_date
      }, config);
      
      toast.success('Task updated successfully');
      setShowEditTaskModal(false);
      setEditingTask(null);
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  const handleReassignTask = async (taskId, newStaffId) => {
    try {
      const token = localStorage.getItem('field_staff_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.put(`${API_URL}/api/field-staff/admin/tasks/${taskId}`, {
        assigned_to: newStaffId
      }, config);
      
      toast.success('Task reassigned successfully');
      fetchDashboardData();
    } catch (error) {
      console.error('Error reassigning task:', error);
      toast.error('Failed to reassign task');
    }
  };

  const openTaskDetailModal = (task) => {
    setSelectedTaskForDetail(task);
    setShowTaskDetailModal(true);
    fetchTaskComments(task.id);
  };

  const fetchTaskComments = async (taskId) => {
    try {
      setLoadingComments(true);
      const token = localStorage.getItem('field_staff_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(
        `${API_URL}/api/field-staff/admin/tasks/${taskId}/comments`,
        config
      );
      setTaskComments(response.data.comments || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleSendAdminComment = async () => {
    if (!newAdminComment.trim() || !selectedTaskForDetail) return;
    
    try {
      setSendingComment(true);
      const token = localStorage.getItem('field_staff_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(
        `${API_URL}/api/field-staff/admin/tasks/${selectedTaskForDetail.id}/comments`,
        { message: newAdminComment },
        config
      );
      
      setNewAdminComment('');
      fetchTaskComments(selectedTaskForDetail.id);
      toast.success('Reply sent to staff');
    } catch (error) {
      console.error('Error sending comment:', error);
      toast.error('Failed to send reply');
    } finally {
      setSendingComment(false);
    }
  };

  const handleVerifyTask = async () => {
    if (!selectedTaskForDetail) return;
    
    try {
      setVerifyingTask(true);
      const token = localStorage.getItem('field_staff_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(
        `${API_URL}/api/field-staff/admin/tasks/${selectedTaskForDetail.id}/verify-completion`,
        {
          notes: verificationNotes,
          payment_amount: paymentAmount ? parseFloat(paymentAmount) : 0
        },
        config
      );
      
      toast.success('Task verified successfully!');
      setShowVerifyModal(false);
      setShowTaskDetailModal(false);
      setVerificationNotes('');
      setPaymentAmount('');
      fetchDashboardData();
    } catch (error) {
      console.error('Error verifying task:', error);
      toast.error(error.response?.data?.detail || 'Failed to verify task');
    } finally {
      setVerifyingTask(false);
    }
  };

  const openPasswordModal = (user, userType) => {
    setSelectedUserForPassword({ ...user, userType });
    setNewPassword('');
    setConfirmPassword('');
    setShowPasswordModal(true);
  };

  const handleChangePassword = async () => {
    if (!selectedUserForPassword) return;
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    try {
      setChangingPassword(true);
      const token = localStorage.getItem('field_staff_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const endpoint = selectedUserForPassword.userType === 'admin' 
        ? `${API_URL}/api/field-staff/super-admin/admins/${selectedUserForPassword.id}/change-password`
        : `${API_URL}/api/field-staff/admin/staff/${selectedUserForPassword.id}/change-password`;
      
      await axios.post(endpoint, { new_password: newPassword }, config);
      
      toast.success(`Password changed for ${selectedUserForPassword.name}`);
      setShowPasswordModal(false);
      setSelectedUserForPassword(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Activity className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Color palette for staff/admin cards based on name
  const getCardGradient = (name, index) => {
    const gradients = [
      'from-blue-500 via-blue-600 to-indigo-600',
      'from-emerald-500 via-green-600 to-teal-600',
      'from-purple-500 via-violet-600 to-indigo-600',
      'from-orange-500 via-amber-600 to-yellow-600',
      'from-pink-500 via-rose-600 to-red-600',
      'from-cyan-500 via-teal-600 to-blue-600',
      'from-indigo-500 via-purple-600 to-pink-600',
      'from-amber-500 via-orange-600 to-red-600',
    ];
    // Use first letter of name to determine color
    const charCode = (name?.charCodeAt(0) || 65) - 65;
    return gradients[(charCode + index) % gradients.length];
  };

  const getCardShadow = (index) => {
    const shadows = [
      'shadow-blue-500/30',
      'shadow-emerald-500/30',
      'shadow-purple-500/30',
      'shadow-orange-500/30',
      'shadow-pink-500/30',
      'shadow-cyan-500/30',
      'shadow-indigo-500/30',
      'shadow-amber-500/30',
    ];
    return shadows[index % shadows.length];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100">
      {/* Header - Matching Screenshot Design */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 shadow-xl">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo in White Card + Welcome */}
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="bg-white rounded-xl sm:rounded-2xl p-2 sm:p-3 shadow-lg">
                <img 
                  src="/netix-logo.png" 
                  alt="Netix Fibernet" 
                  className="h-10 w-10 sm:h-12 sm:w-12 md:h-14 md:w-14 object-contain"
                />
              </div>
              <div className="text-white">
                <h1 className="text-base sm:text-xl md:text-2xl font-bold">Field Staff Manager</h1>
                <p className="text-blue-100 text-xs sm:text-sm">
                  Welcome, {user?.name || (user?.role === 'super_admin' ? 'Super Admin' : 'Admin')}!
                  <span className="ml-1 opacity-75">({user?.role === 'super_admin' ? 'Super Admin' : 'Admin'})</span>
                </p>
              </div>
            </div>
            {/* Right side buttons */}
            <div className="flex items-center gap-1 sm:gap-2">
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 p-2">
                <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 p-2 sm:px-3" onClick={handleLogout}>
                <LogOut className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden sm:inline ml-2">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Mobile Optimized */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Stats Grid - 3D Colorful Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
          {/* Total Staff Card */}
          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl blur-lg opacity-50 group-hover:opacity-70 transition-opacity"></div>
            <Card className="relative bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 text-white border-0 rounded-2xl overflow-hidden transform hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 shadow-xl">
              <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <CardContent className="p-4 sm:p-5 md:p-6 relative">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-blue-100">Total Staff</p>
                    <p className="text-2xl sm:text-3xl md:text-4xl font-bold mt-1 sm:mt-2">{stats?.total_staff || 0}</p>
                    <p className="text-xs text-blue-200 mt-0.5 sm:mt-1">{stats?.active_staff || 0} active</p>
                  </div>
                  <div className="p-2 sm:p-3 md:p-4 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl">
                    <Users className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Active Tasks Card */}
          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-amber-600 rounded-2xl blur-lg opacity-50 group-hover:opacity-70 transition-opacity"></div>
            <Card className="relative bg-gradient-to-br from-orange-500 via-orange-600 to-amber-600 text-white border-0 rounded-2xl overflow-hidden transform hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 shadow-xl">
              <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <CardContent className="p-4 sm:p-5 md:p-6 relative">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-orange-100">Active Tasks</p>
                    <p className="text-2xl sm:text-3xl md:text-4xl font-bold mt-1 sm:mt-2">{stats?.in_progress_tasks || 0}</p>
                    <p className="text-xs text-orange-200 mt-0.5 sm:mt-1">In progress</p>
                  </div>
                  <div className="p-2 sm:p-3 md:p-4 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl">
                    <Activity className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Completed Card */}
          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-green-600 rounded-2xl blur-lg opacity-50 group-hover:opacity-70 transition-opacity"></div>
            <Card className="relative bg-gradient-to-br from-emerald-500 via-green-600 to-teal-600 text-white border-0 rounded-2xl overflow-hidden transform hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 shadow-xl">
              <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <CardContent className="p-4 sm:p-5 md:p-6 relative">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-green-100">Completed</p>
                    <p className="text-2xl sm:text-3xl md:text-4xl font-bold mt-1 sm:mt-2">{stats?.completed_tasks || 0}</p>
                    <p className="text-xs text-green-200 mt-0.5 sm:mt-1">This week</p>
                  </div>
                  <div className="p-2 sm:p-3 md:p-4 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl">
                    <CheckCircle className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Overdue Card */}
          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-rose-600 rounded-2xl blur-lg opacity-50 group-hover:opacity-70 transition-opacity"></div>
            <Card className="relative bg-gradient-to-br from-red-500 via-rose-600 to-pink-600 text-white border-0 rounded-2xl overflow-hidden transform hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 shadow-xl">
              <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <CardContent className="p-4 sm:p-5 md:p-6 relative">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-red-100">Overdue</p>
                    <p className="text-2xl sm:text-3xl md:text-4xl font-bold mt-1 sm:mt-2">{stats?.overdue_tasks || 0}</p>
                    <p className="text-xs text-red-200 mt-0.5 sm:mt-1">Need attention</p>
                  </div>
                  <div className="p-2 sm:p-3 md:p-4 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl">
                    <AlertCircle className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Action Buttons - Mobile Optimized */}
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6">
          <Button 
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-xs sm:text-sm shadow-lg shadow-blue-500/30 transform hover:scale-105 transition-all"
            onClick={() => setShowStaffModal(true)}
          >
            <UserPlus className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            <span className="hidden xs:inline">Add</span> Staff
          </Button>
          
          {user?.role === 'super_admin' && (
            <Button 
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-xs sm:text-sm shadow-lg shadow-purple-500/30 transform hover:scale-105 transition-all"
              onClick={() => setShowAdminModal(true)}
            >
              <Shield className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Add</span> Admin
            </Button>
          )}

          <Button 
            variant="outline"
            className="text-xs sm:text-sm border-2 hover:bg-blue-50 transform hover:scale-105 transition-all"
            onClick={() => setShowTaskModal(true)}
          >
            <ClipboardList className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            <span className="hidden xs:inline">Create</span> Task
          </Button>
        </div>

        {/* View Selector - Mobile Grid / Desktop Scroll */}
        <div className="bg-white rounded-2xl shadow-lg p-2 sm:p-3 mb-4 sm:mb-6">
          <div className="grid grid-cols-4 sm:flex sm:flex-wrap gap-1.5 sm:gap-2">
            <Button
              size="sm"
              variant={activeView === 'overview' ? 'default' : 'ghost'}
              onClick={() => setActiveView('overview')}
              className={`flex flex-col sm:flex-row items-center justify-center h-auto py-2 px-1.5 sm:px-3 sm:py-2 text-[10px] sm:text-sm rounded-xl transition-all ${
                activeView === 'overview' 
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg' 
                  : 'hover:bg-gray-100'
              }`}
            >
              <Activity className="h-4 w-4 sm:h-4 sm:w-4 mb-0.5 sm:mb-0 sm:mr-1.5" />
              <span>Overview</span>
            </Button>
            <Button
              size="sm"
              variant={activeView === 'staff' ? 'default' : 'ghost'}
              onClick={() => setActiveView('staff')}
              className={`flex flex-col sm:flex-row items-center justify-center h-auto py-2 px-1.5 sm:px-3 sm:py-2 text-[10px] sm:text-sm rounded-xl transition-all ${
                activeView === 'staff' 
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg' 
                  : 'hover:bg-gray-100'
              }`}
            >
              <Users className="h-4 w-4 sm:h-4 sm:w-4 mb-0.5 sm:mb-0 sm:mr-1.5" />
              <span>Staff ({staff.length})</span>
            </Button>
            <Button
              size="sm"
              variant={activeView === 'tasks' ? 'default' : 'ghost'}
              onClick={() => setActiveView('tasks')}
              className={`flex flex-col sm:flex-row items-center justify-center h-auto py-2 px-1.5 sm:px-3 sm:py-2 text-[10px] sm:text-sm rounded-xl transition-all ${
                activeView === 'tasks' 
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg' 
                  : 'hover:bg-gray-100'
              }`}
            >
              <ClipboardList className="h-4 w-4 sm:h-4 sm:w-4 mb-0.5 sm:mb-0 sm:mr-1.5" />
              <span>Tasks ({tasks.length})</span>
            </Button>
            <Button
              size="sm"
              variant={activeView === 'map' ? 'default' : 'ghost'}
              onClick={() => setShowLiveMap(true)}
              className={`flex flex-col sm:flex-row items-center justify-center h-auto py-2 px-1.5 sm:px-3 sm:py-2 text-[10px] sm:text-sm rounded-xl transition-all hover:bg-gray-100`}
            >
              <MapPin className="h-4 w-4 sm:h-4 sm:w-4 mb-0.5 sm:mb-0 sm:mr-1.5" />
              <span>Map</span>
            </Button>
            <Button
              size="sm"
              variant={activeView === 'activity' ? 'default' : 'ghost'}
              onClick={() => setShowActivityMap(true)}
              className={`flex flex-col sm:flex-row items-center justify-center h-auto py-2 px-1.5 sm:px-3 sm:py-2 text-[10px] sm:text-sm rounded-xl transition-all hover:bg-gray-100`}
            >
              <Route className="h-4 w-4 sm:h-4 sm:w-4 mb-0.5 sm:mb-0 sm:mr-1.5" />
              <span>Activity</span>
            </Button>
            <Button
              size="sm"
              variant={activeView === 'directory' ? 'default' : 'ghost'}
              onClick={() => setActiveView('directory')}
              className={`flex flex-col sm:flex-row items-center justify-center h-auto py-2 px-1.5 sm:px-3 sm:py-2 text-[10px] sm:text-sm rounded-xl transition-all ${
                activeView === 'directory' 
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg' 
                  : 'hover:bg-gray-100'
              }`}
            >
              <BookUser className="h-4 w-4 sm:h-4 sm:w-4 mb-0.5 sm:mb-0 sm:mr-1.5" />
              <span>Directory</span>
            </Button>
            <Button
              size="sm"
              variant={activeView === 'infield' ? 'default' : 'ghost'}
              onClick={() => setActiveView('infield')}
              className={`flex flex-col sm:flex-row items-center justify-center h-auto py-2 px-1.5 sm:px-3 sm:py-2 text-[10px] sm:text-sm rounded-xl transition-all ${
                activeView === 'infield' 
                  ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white shadow-lg' 
                  : 'hover:bg-orange-50'
              }`}
            >
              <MapPinned className="h-4 w-4 sm:h-4 sm:w-4 mb-0.5 sm:mb-0 sm:mr-1.5" />
              <span>In Field ({inFieldTasks.length})</span>
            </Button>
            {user?.role === 'super_admin' && (
              <Button
                size="sm"
                variant={activeView === 'admins' ? 'default' : 'ghost'}
                onClick={() => setActiveView('admins')}
                className={`flex flex-col sm:flex-row items-center justify-center h-auto py-2 px-1.5 sm:px-3 sm:py-2 text-[10px] sm:text-sm rounded-xl transition-all ${
                  activeView === 'admins' 
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg' 
                    : 'hover:bg-purple-50'
                }`}
              >
                <Shield className="h-4 w-4 sm:h-4 sm:w-4 mb-0.5 sm:mb-0 sm:mr-1.5" />
                <span>Admins ({admins.length})</span>
              </Button>
            )}
          </div>
        </div>

        {/* Content Area */}
        {activeView === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Advanced Live Staff Locations */}
            <Card className="rounded-2xl shadow-xl border-0 lg:col-span-2">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl">
                      <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                    </div>
                    Live Staff Locations
                    <span className="ml-2 flex items-center gap-1">
                      <span className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></span>
                      <span className="text-xs text-green-600 font-normal">Live</span>
                    </span>
                  </CardTitle>
                  <Badge className="bg-blue-100 text-blue-700 text-xs">{Array.isArray(locations) ? locations.length : 0} Tracked</Badge>
                </div>
              </CardHeader>
              <CardContent className="p-3 sm:p-6">
                {(Array.isArray(locations) && locations.length > 0) || staff.length > 0 ? (
                  <div className="space-y-3">
                    {/* Staff Location Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {staff.map((member, index) => {
                        // Find location for this staff member (with safety check)
                        const staffLocation = Array.isArray(locations) 
                          ? locations.find(loc => loc.staff_id === member.id || loc.user_id === member.id)
                          : null;
                        const isLive = staffLocation && staffLocation.updated_at && 
                          (new Date() - new Date(staffLocation.updated_at)) < 5 * 60 * 1000; // 5 minutes
                        const lastUpdate = staffLocation?.updated_at ? new Date(staffLocation.updated_at) : null;
                        
                        return (
                          <div key={member.id} className="group relative">
                            <div className={`absolute inset-0 rounded-xl blur-md opacity-20 group-hover:opacity-40 transition-opacity ${isLive ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                            <div className={`relative p-3 sm:p-4 rounded-xl border-2 transition-all hover:shadow-lg ${
                              isLive 
                                ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' 
                                : 'bg-gradient-to-br from-gray-50 to-slate-50 border-gray-200'
                            }`}>
                              <div className="flex items-start gap-3">
                                {/* Avatar */}
                                <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-xl flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-lg ${
                                  isLive 
                                    ? 'bg-gradient-to-br from-green-500 to-emerald-600' 
                                    : 'bg-gradient-to-br from-gray-400 to-gray-500'
                                }`}>
                                  {member.name?.charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="font-semibold text-sm sm:text-base truncate">{member.name}</h4>
                                    {/* Live/Last Location Badge */}
                                    <Badge className={`text-[10px] sm:text-xs ${
                                      isLive 
                                        ? 'bg-green-100 text-green-700 border border-green-300' 
                                        : 'bg-gray-100 text-gray-600 border border-gray-300'
                                    }`}>
                                      <span className={`h-1.5 w-1.5 rounded-full mr-1 ${isLive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></span>
                                      {isLive ? 'LIVE' : 'Last Location'}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-gray-600 truncate">{member.position || 'Field Staff'}</p>
                                  
                                  {/* Location Info */}
                                  {staffLocation ? (
                                    <div className="mt-2 space-y-1">
                                      <div className="flex items-center gap-1 text-xs text-gray-600">
                                        <MapPin className="h-3 w-3" />
                                        <span className="truncate">
                                          {staffLocation.address || `${staffLocation.latitude?.toFixed(4)}, ${staffLocation.longitude?.toFixed(4)}`}
                                        </span>
                                      </div>
                                      {lastUpdate && (
                                        <p className="text-[10px] text-gray-500">
                                          Updated: {lastUpdate.toLocaleTimeString()} ({isLive ? 'Just now' : lastUpdate.toLocaleDateString()})
                                        </p>
                                      )}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-400 mt-2 italic">Location not available</p>
                                  )}
                                </div>
                                
                                {/* Actions */}
                                <div className="flex flex-col gap-1">
                                  {staffLocation?.latitude && staffLocation?.longitude && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 w-7 sm:h-8 sm:w-8 p-0 text-blue-600 hover:bg-blue-50"
                                      onClick={() => window.open(`https://www.google.com/maps?q=${staffLocation.latitude},${staffLocation.longitude}`, '_blank')}
                                    >
                                      <Navigation className="h-3 w-3 sm:h-4 sm:w-4" />
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 w-7 sm:h-8 sm:w-8 p-0 text-green-600 hover:bg-green-50"
                                    onClick={() => handleCall(member.mobile)}
                                    disabled={!member.mobile}
                                  >
                                    <Phone className="h-3 w-3 sm:h-4 sm:w-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Summary Stats */}
                    <div className="flex flex-wrap gap-2 sm:gap-4 pt-3 border-t">
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg">
                        <span className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></span>
                        <span className="text-xs sm:text-sm text-green-700 font-medium">
                          {Array.isArray(locations) ? locations.filter(loc => (new Date() - new Date(loc.updated_at)) < 5 * 60 * 1000).length : 0} Live Now
                        </span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg">
                        <Users className="h-3 w-3 text-blue-600" />
                        <span className="text-xs sm:text-sm text-blue-700 font-medium">{staff.length} Total Staff</span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg">
                        <MapPinned className="h-3 w-3 text-gray-600" />
                        <span className="text-xs sm:text-sm text-gray-700 font-medium">{Array.isArray(locations) ? locations.length : 0} Locations Tracked</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 sm:p-8 text-center">
                    <div className="bg-white/80 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Navigation className="h-8 w-8 text-blue-400" />
                    </div>
                    <h3 className="font-semibold text-gray-700 mb-2">No Staff Locations Available</h3>
                    <p className="text-sm text-gray-600">Staff location tracking will appear here when staff enable location sharing</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="rounded-2xl shadow-xl border-0">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl">
                    <Activity className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6">
                <div className="space-y-2 sm:space-y-3">
                  {tasks.slice(0, 5).map((task, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 sm:p-3 hover:bg-gray-50 rounded-xl transition-colors">
                      <div className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${
                        task.status === 'completed' ? 'bg-green-500' :
                        task.status === 'in_progress' ? 'bg-orange-500' :
                        'bg-blue-500'
                      }`}></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-medium truncate">{task.title}</p>
                        <p className="text-[10px] sm:text-xs text-gray-500">
                          {task.assigned_to_name} • {new Date(task.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-[10px] sm:text-xs">{task.priority}</Badge>
                    </div>
                  ))}
                  {tasks.length === 0 && (
                    <p className="text-center text-gray-500 py-4 text-sm">No recent activity</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats Card */}
            <Card className="rounded-2xl shadow-xl border-0">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl">
                    <ClipboardList className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  Task Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
                    <span className="text-sm font-medium text-gray-700">Total Tasks</span>
                    <span className="text-lg font-bold text-blue-600">{tasks.length}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl">
                    <span className="text-sm font-medium text-gray-700">In Progress</span>
                    <span className="text-lg font-bold text-orange-600">{stats?.in_progress_tasks || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
                    <span className="text-sm font-medium text-gray-700">Completed</span>
                    <span className="text-lg font-bold text-green-600">{stats?.completed_tasks || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-red-50 to-rose-50 rounded-xl">
                    <span className="text-sm font-medium text-gray-700">Overdue</span>
                    <span className="text-lg font-bold text-red-600">{stats?.overdue_tasks || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeView === 'staff' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {staff.map((member, index) => (
              <div key={member.id} className="group relative">
                {/* 3D Shadow Effect */}
                <div className={`absolute inset-0 bg-gradient-to-r ${getCardGradient(member.name, index)} rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity`}></div>
                <Card className="relative bg-white border-0 rounded-2xl overflow-hidden transform hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 shadow-xl">
                  {/* Decorative gradient bar */}
                  <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${getCardGradient(member.name, index)}`}></div>
                  <CardContent className="p-4 sm:p-6 pt-5 sm:pt-7">
                    <div className="flex items-start gap-3 sm:gap-4">
                      {/* Avatar with gradient based on name */}
                      <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-xl sm:rounded-2xl bg-gradient-to-br ${getCardGradient(member.name, index)} flex items-center justify-center text-white font-bold text-base sm:text-lg flex-shrink-0 shadow-lg ${getCardShadow(index)}`}>
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm sm:text-base truncate">{member.name}</h3>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">{member.position}</p>
                        <div className="flex items-center gap-1 sm:gap-2 mt-1.5 sm:mt-2">
                          <Badge 
                            variant={member.is_active ? 'success' : 'secondary'} 
                            className={`text-[10px] sm:text-xs ${member.is_active ? 'bg-emerald-100 text-emerald-700' : ''}`}
                          >
                            {member.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          {member.location_sharing_consent && (
                            <div className="flex items-center gap-0.5 text-green-600">
                              <MapPin className="h-3 w-3" />
                              <span className="text-[10px] hidden sm:inline">GPS</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1.5 sm:gap-2 mt-3 sm:mt-4">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 h-8 sm:h-9 text-xs sm:text-sm hover:bg-green-50 hover:border-green-300 hover:text-green-700 transition-colors"
                        onClick={() => handleCall(member.mobile)}
                        disabled={!member.mobile}
                      >
                        <Phone className="h-3 w-3 sm:h-4 sm:w-4 mr-0.5 sm:mr-1" />
                        Call
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 h-8 sm:h-9 text-xs sm:text-sm hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors"
                      >
                        <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-0.5 sm:mr-1" />
                        View
                      </Button>
                      {user?.role === 'super_admin' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 sm:h-9 px-2 sm:px-3 text-purple-600 hover:bg-purple-50 hover:border-purple-300 transition-colors"
                          onClick={() => openPasswordModal(member, 'staff')}
                          data-testid={`change-password-staff-${member.id}`}
                        >
                          <Key className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      )}
                      {user?.role === 'super_admin' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 sm:h-9 px-2 sm:px-3 text-red-600 hover:bg-red-50 hover:border-red-300 transition-colors"
                          onClick={() => openDeleteConfirm(member, 'staff')}
                          data-testid={`delete-staff-${member.id}`}
                        >
                          <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
            {staff.length === 0 && (
              <Card className="col-span-full rounded-2xl shadow-xl">
                <CardContent className="p-8 sm:p-12 text-center">
                  <div className="bg-gradient-to-br from-blue-100 to-indigo-100 w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Users className="h-8 w-8 sm:h-10 sm:w-10 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-700 mb-2 text-sm sm:text-base">No staff members yet</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mb-4">Add your first staff member to get started</p>
                  <Button onClick={() => setShowStaffModal(true)} className="bg-gradient-to-r from-blue-600 to-indigo-600">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Staff Member
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeView === 'tasks' && (
          <div className="space-y-4">
            {tasks.map((task) => (
              <Card key={task.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{task.title}</h3>
                        <Badge className={`${
                          task.status === 'completed' ? 'bg-green-100 text-green-700' :
                          task.status === 'verified' ? 'bg-purple-100 text-purple-700' :
                          task.status === 'in_progress' ? 'bg-orange-100 text-orange-700' :
                          task.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {task.status}
                        </Badge>
                        <Badge variant="outline">{task.priority}</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{task.description}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {task.assigned_to_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {new Date(task.due_date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 ml-4">
                      {/* Edit Button - For Super Admin and Admins */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditTask(task)}
                        className="flex items-center gap-1"
                      >
                        <Edit className="h-4 w-4" />
                        Edit
                      </Button>
                      {/* View Details */}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openTaskDetailModal(task)}
                        className="flex items-center gap-1"
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </Button>
                      {/* Delete Button - Only for Super Admin */}
                      {user?.role === 'super_admin' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeleteTaskModal({ show: true, task })}
                          className="flex items-center gap-1 text-red-600 hover:bg-red-50 border-red-200"
                          data-testid={`delete-task-${task.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {tasks.length === 0 && (
              <Card>
                <CardContent className="p-12 text-center">
                  <ClipboardList className="h-16 w-16 text-gray-400 mx-auto mb-3" />
                  <h3 className="font-semibold text-gray-700 mb-2">No tasks yet</h3>
                  <p className="text-sm text-gray-600">Create your first task to get started</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeView === 'map' && (
          <Card>
            <CardContent className="p-0">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg h-[600px] flex items-center justify-center border-2 border-dashed border-blue-200">
                <div className="text-center p-8">
                  <MapPin className="h-20 w-20 text-blue-400 mx-auto mb-4" />
                  <h3 className="font-semibold text-xl text-gray-700 mb-2">Real-time Location Tracking</h3>
                  <p className="text-gray-600 mb-4">Track all {staff.length} staff members on live map</p>
                  <p className="text-sm text-gray-500">Map integration: Google Maps / Mapbox</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {activeView === 'admins' && user?.role === 'super_admin' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {admins.map((admin, index) => (
              <div key={admin.id} className="group relative">
                {/* 3D Shadow Effect */}
                <div className={`absolute inset-0 bg-gradient-to-r ${admin.role === 'super_admin' ? 'from-purple-500 to-indigo-600' : 'from-indigo-500 to-blue-600'} rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity`}></div>
                <Card className="relative bg-white border-0 rounded-2xl overflow-hidden transform hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 shadow-xl">
                  {/* Decorative gradient bar */}
                  <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${admin.role === 'super_admin' ? 'from-purple-500 via-violet-600 to-indigo-600' : 'from-indigo-500 via-blue-600 to-cyan-600'}`}></div>
                  <CardContent className="p-4 sm:p-6 pt-5 sm:pt-7">
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-xl sm:rounded-2xl bg-gradient-to-br ${admin.role === 'super_admin' ? 'from-purple-500 to-indigo-600' : 'from-indigo-500 to-blue-600'} flex items-center justify-center text-white font-bold text-base sm:text-lg flex-shrink-0 shadow-lg`}>
                        {admin.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm sm:text-base truncate">{admin.name}</h3>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">{admin.email}</p>
                        <Badge className={`mt-1.5 sm:mt-2 text-[10px] sm:text-xs ${admin.role === 'super_admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                          {admin.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-1.5 sm:gap-2 mt-3 sm:mt-4">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 h-8 sm:h-9 text-xs sm:text-sm text-purple-600 hover:bg-purple-50 hover:border-purple-300 transition-colors"
                        onClick={() => openPasswordModal(admin, 'admin')}
                        data-testid={`change-password-admin-${admin.id}`}
                      >
                        <Key className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                        Password
                      </Button>
                      {admin.role !== 'super_admin' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 sm:h-9 px-2 sm:px-3 text-red-600 hover:bg-red-50 hover:border-red-300 transition-colors"
                          onClick={() => openDeleteConfirm(admin, 'admin')}
                          data-testid={`delete-admin-${admin.id}`}
                        >
                          <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
            {admins.length === 0 && (
              <Card className="col-span-full rounded-2xl shadow-xl">
                <CardContent className="p-8 sm:p-12 text-center">
                  <div className="bg-gradient-to-br from-purple-100 to-indigo-100 w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Shield className="h-8 w-8 sm:h-10 sm:w-10 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-700 mb-2 text-sm sm:text-base">No admins yet</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mb-4">Add your first admin to help manage</p>
                  <Button onClick={() => setShowAdminModal(true)} className="bg-gradient-to-r from-purple-600 to-indigo-600">
                    <Shield className="h-4 w-4 mr-2" />
                    Add Admin
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Staff Directory View */}
        {activeView === 'directory' && (
          <Card className="rounded-2xl shadow-xl border-0">
            <CardHeader className="pb-2 sm:pb-4">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <BookUser className="h-4 w-4 sm:h-5 sm:w-5" />
                Staff Directory
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 sm:space-y-6">
                {/* Super Admins */}
                {allUsers.filter(u => u.userType === 'super_admin').length > 0 && (
                  <div>
                    <h3 className="text-sm sm:text-lg font-semibold text-purple-700 mb-2 sm:mb-3 flex items-center gap-2">
                      <Shield className="h-4 w-4 sm:h-5 sm:w-5" />
                      Super Admins
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                      {allUsers.filter(u => u.userType === 'super_admin').map((person, index) => (
                        <div key={person.id} className="group relative">
                          <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl blur-md opacity-20 group-hover:opacity-40 transition-opacity"></div>
                          <Card className="relative border-0 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl overflow-hidden transform hover:scale-[1.02] transition-all duration-300 shadow-lg">
                          <CardContent className="p-3 sm:p-4">
                            <div className="flex items-center gap-2 sm:gap-3">
                              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl sm:rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-lg">
                                {person.name?.charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-sm sm:text-base truncate">{person.name}</h4>
                                <p className="text-xs sm:text-sm text-gray-600 truncate">{person.email}</p>
                                <Badge className="mt-1 text-[10px] sm:text-xs bg-purple-100 text-purple-700">Super Admin</Badge>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 w-8 sm:h-9 sm:w-9 p-0 bg-green-50 text-green-600 hover:bg-green-100 rounded-full shadow-md"
                                onClick={() => handleCall(person.mobile || person.phone)}
                                disabled={!person.mobile && !person.phone}
                              >
                                <PhoneCall className="h-4 w-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Admins */}
                {allUsers.filter(u => u.userType === 'admin').length > 0 && (
                  <div>
                    <h3 className="text-sm sm:text-lg font-semibold text-blue-700 mb-2 sm:mb-3 flex items-center gap-2">
                      <UserCheck className="h-4 w-4 sm:h-5 sm:w-5" />
                      Admins
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                      {allUsers.filter(u => u.userType === 'admin').map((person, index) => (
                        <div key={person.id} className="group relative">
                          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl blur-md opacity-20 group-hover:opacity-40 transition-opacity"></div>
                          <Card className="relative border-0 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl overflow-hidden transform hover:scale-[1.02] transition-all duration-300 shadow-lg">
                          <CardContent className="p-3 sm:p-4">
                            <div className="flex items-center gap-2 sm:gap-3">
                              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl sm:rounded-full bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-lg">
                                {person.name?.charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-sm sm:text-base truncate">{person.name}</h4>
                                <p className="text-xs sm:text-sm text-gray-600 truncate">{person.email}</p>
                                <Badge className="mt-1 text-[10px] sm:text-xs bg-blue-100 text-blue-700">Admin</Badge>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 w-8 sm:h-9 sm:w-9 p-0 bg-green-50 text-green-600 hover:bg-green-100 rounded-full shadow-md"
                                onClick={() => handleCall(person.mobile || person.phone)}
                                disabled={!person.mobile && !person.phone}
                              >
                                <PhoneCall className="h-4 w-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Staff by Position */}
                {(() => {
                  const staffOnly = allUsers.filter(u => u.userType === 'staff');
                  const positions = [...new Set(staffOnly.map(s => s.position || 'Staff'))];
                  return positions.map((position, posIndex) => (
                    <div key={position}>
                      <h3 className="text-sm sm:text-lg font-semibold text-green-700 mb-2 sm:mb-3 flex items-center gap-2">
                        <Users className="h-4 w-4 sm:h-5 sm:w-5" />
                        {position}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                        {staffOnly.filter(s => (s.position || 'Staff') === position).map((person, index) => (
                          <div key={person.id} className="group relative">
                            <div className={`absolute inset-0 bg-gradient-to-r ${getCardGradient(person.name, index)} rounded-xl blur-md opacity-20 group-hover:opacity-40 transition-opacity`}></div>
                            <Card className="relative border-0 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl overflow-hidden transform hover:scale-[1.02] transition-all duration-300 shadow-lg">
                            <CardContent className="p-3 sm:p-4">
                              <div className="flex items-center gap-2 sm:gap-3">
                                <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-xl sm:rounded-full bg-gradient-to-br ${getCardGradient(person.name, index)} flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-lg`}>
                                  {person.name?.charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold text-sm sm:text-base truncate">{person.name}</h4>
                                  <p className="text-xs sm:text-sm text-gray-600 truncate">{person.email}</p>
                                  <div className="flex items-center gap-1 sm:gap-2 mt-1 flex-wrap">
                                    <Badge variant="outline" className="text-[10px] sm:text-xs text-green-600 border-green-300">{position}</Badge>
                                    {person.is_active ? (
                                      <Badge className="text-[10px] sm:text-xs bg-green-100 text-green-700">Active</Badge>
                                    ) : (
                                      <Badge variant="secondary" className="text-[10px] sm:text-xs">Inactive</Badge>
                                    )}
                                  </div>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 w-8 sm:h-9 sm:w-9 p-0 bg-green-50 text-green-600 hover:bg-green-100 rounded-full shadow-md"
                                  onClick={() => handleCall(person.mobile || person.phone)}
                                  disabled={!person.mobile && !person.phone}
                                >
                                  <PhoneCall className="h-4 w-4" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}

                {allUsers.length === 0 && (
                  <div className="text-center py-8 sm:py-12">
                    <div className="bg-gradient-to-br from-gray-100 to-slate-100 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <BookUser className="h-7 w-7 sm:h-8 sm:w-8 text-gray-400" />
                    </div>
                    <h3 className="font-semibold text-gray-700 mb-2 text-sm sm:text-base">No users found</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Add staff or admins to see them here</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* In Field View */}
        {activeView === 'infield' && (
          <Card className="rounded-2xl shadow-xl border-0">
            <CardHeader className="pb-2 sm:pb-4">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <MapPinned className="h-4 w-4 sm:h-5 sm:w-5" />
                In Field Tasks
                <Badge className="ml-2 text-xs sm:text-sm bg-orange-100 text-orange-700">{inFieldTasks.length} Active</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-6">
              {inFieldTasks.length > 0 ? (
                <div className="space-y-3 sm:space-y-4">
                  {inFieldTasks.map((task, index) => (
                    <div key={task.id} className="group relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-amber-600 rounded-2xl blur-lg opacity-20 group-hover:opacity-40 transition-opacity"></div>
                      <Card className="relative border-0 border-l-4 border-l-orange-500 rounded-2xl overflow-hidden transform hover:scale-[1.01] transition-all duration-300 shadow-xl bg-white">
                      <CardContent className="p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2">
                              <h4 className="font-semibold text-sm sm:text-lg">{task.title}</h4>
                              <Badge className={`text-[10px] sm:text-xs ${task.priority === 'high' || task.priority === 'urgent' ? 'bg-red-100 text-red-700' : task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}`}>
                                {task.priority}
                              </Badge>
                              <Badge className="text-[10px] sm:text-xs bg-orange-100 text-orange-700">
                                <MapPinned className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                                In Field
                              </Badge>
                            </div>
                            <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3 line-clamp-2">{task.description}</p>
                            <div className="grid grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm">
                              <div>
                                <span className="text-gray-500 block">Assigned To:</span>
                                <p className="font-medium truncate">{task.assigned_to_name}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 block">Due Date:</span>
                                <p className="font-medium">{task.due_date ? new Date(task.due_date).toLocaleDateString() : 'N/A'}</p>
                              </div>
                            </div>
                            {task.customer_contact && (
                              <div className="mt-2 sm:mt-3 p-2 sm:p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
                                <p className="text-xs sm:text-sm"><strong>Address:</strong> {task.customer_contact.address || 'N/A'}</p>
                                <p className="text-xs sm:text-sm"><strong>Phone:</strong> {task.customer_contact.phone || 'N/A'}</p>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-row sm:flex-col gap-1.5 sm:gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs sm:text-sm hover:bg-blue-50 hover:border-blue-300"
                              onClick={() => handleEditTask(task)}
                            >
                              <Edit className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                              Edit
                            </Button>
                            {task.customer_contact?.phone && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 text-xs sm:text-sm text-green-600 hover:bg-green-50 hover:border-green-300"
                                onClick={() => handleCall(task.customer_contact.phone)}
                              >
                                <Phone className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                Call
                              </Button>
                            )}
                            {task.customer_contact?.latitude && task.customer_contact?.longitude && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 text-xs sm:text-sm text-blue-600 hover:bg-blue-50 hover:border-blue-300"
                                onClick={() => window.open(`https://www.google.com/maps?q=${task.customer_contact.latitude},${task.customer_contact.longitude}`, '_blank')}
                              >
                                <Navigation className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                Navigate
                              </Button>
                            )}
                            {/* Delete Button - Only for Super Admin */}
                            {user?.role === 'super_admin' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setDeleteTaskModal({ show: true, task })}
                                className="h-8 text-xs sm:text-sm text-red-600 hover:bg-red-50 border-red-200"
                                data-testid={`delete-infield-task-${task.id}`}
                              >
                                <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                Delete
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 sm:py-12">
                  <div className="bg-gradient-to-br from-orange-100 to-amber-100 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <MapPinned className="h-7 w-7 sm:h-8 sm:w-8 text-orange-500" />
                  </div>
                  <h3 className="font-semibold text-gray-700 mb-2 text-sm sm:text-base">No tasks in field</h3>
                  <p className="text-xs sm:text-sm text-gray-600">Tasks with &quot;In Progress&quot; status will appear here</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal.show && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-3 sm:p-4">
          <Card className="w-full max-w-md rounded-2xl shadow-2xl">
            <CardHeader className="bg-red-50 border-b rounded-t-2xl">
              <CardTitle className="flex items-center gap-2 text-red-700 text-base sm:text-lg">
                <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                Confirm Delete
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <p className="text-gray-700 mb-3 sm:mb-4 text-sm sm:text-base">
                Are you sure you want to delete <strong>{deleteConfirmModal.user?.name}</strong>?
              </p>
              <p className="text-xs sm:text-sm text-gray-500 mb-4">
                This action cannot be undone. All associated data will be permanently removed.
              </p>
              <div className="flex gap-2 sm:gap-3 justify-end">
                <Button
                  variant="outline"
                  className="text-xs sm:text-sm h-9 sm:h-10"
                  onClick={() => setDeleteConfirmModal({ show: false, user: null, type: null })}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  className="text-xs sm:text-sm h-9 sm:h-10"
                  onClick={handleDeleteUser}
                >
                  <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Task Confirmation Modal (Super Admin Only) */}
      {deleteTaskModal.show && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="bg-red-50 border-b">
              <CardTitle className="flex items-center gap-2 text-red-700">
                <Trash2 className="h-5 w-5" />
                Delete Task
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete this task?
              </p>
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="font-semibold">{deleteTaskModal.task?.title}</p>
                <p className="text-sm text-gray-600">Assigned to: {deleteTaskModal.task?.assigned_to_name}</p>
                <p className="text-sm text-gray-500">Status: {deleteTaskModal.task?.status}</p>
              </div>
              <p className="text-sm text-red-600 mb-4">
                ⚠️ This action cannot be undone. The task and all its comments will be permanently deleted.
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setDeleteTaskModal({ show: false, task: null })}
                  disabled={deletingTask}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteTask}
                  disabled={deletingTask}
                  data-testid="confirm-delete-task-btn"
                >
                  {deletingTask ? (
                    <>
                      <Activity className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Task
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modals */}
      <CreateStaffModal 
        open={showStaffModal} 
        onClose={() => setShowStaffModal(false)}
        onSuccess={fetchDashboardData}
      />
      
      {user?.role === 'super_admin' && (
        <CreateAdminModal 
          open={showAdminModal} 
          onClose={() => setShowAdminModal(false)}
          onSuccess={fetchDashboardData}
        />
      )}

      <TaskAssignmentModal 
        isOpen={showTaskModal} 
        onClose={() => setShowTaskModal(false)}
        onTaskCreated={fetchDashboardData}
      />

      {/* Live Location Map Modal */}
      {showLiveMap && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-6xl max-h-[90vh] overflow-auto">
            <EnhancedLiveLocationMap onClose={() => setShowLiveMap(false)} />
          </div>
        </div>
      )}

      {/* Activity Maps Modal */}
      {showActivityMap && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-6xl max-h-[90vh] overflow-auto">
            <EnhancedActivityMaps onClose={() => setShowActivityMap(false)} />
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {showEditTaskModal && editingTask && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-t-xl flex items-center justify-between">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Edit Task
              </h2>
              <button 
                onClick={() => { setShowEditTaskModal(false); setEditingTask(null); }}
                className="p-2 hover:bg-white/20 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <Input
                  value={editingTask.title || ''}
                  onChange={(e) => setEditingTask({...editingTask, title: e.target.value})}
                  placeholder="Task title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editingTask.description || ''}
                  onChange={(e) => setEditingTask({...editingTask, description: e.target.value})}
                  placeholder="Task description"
                  className="w-full border rounded-lg p-2 min-h-[100px]"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={editingTask.priority || 'medium'}
                    onChange={(e) => setEditingTask({...editingTask, priority: e.target.value})}
                    className="w-full border rounded-lg p-2"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editingTask.status || 'pending'}
                    onChange={(e) => setEditingTask({...editingTask, status: e.target.value})}
                    className="w-full border rounded-lg p-2"
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="verified">Verified</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                <select
                  value={editingTask.assigned_to || ''}
                  onChange={(e) => setEditingTask({...editingTask, assigned_to: e.target.value})}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Select Assignee</option>
                  {admins.filter(a => a.role === 'admin').length > 0 && (
                    <optgroup label="👔 Admins">
                      {admins.filter(a => a.role === 'admin').map((admin) => (
                        <option key={admin.id} value={admin.id}>
                          {admin.name} - Admin
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {staff.length > 0 && (
                    <optgroup label="👷 Staff Members">
                      {staff.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} - {s.position || 'Staff'}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <Input
                  type="date"
                  value={editingTask.due_date ? new Date(editingTask.due_date).toISOString().split('T')[0] : ''}
                  onChange={(e) => setEditingTask({...editingTask, due_date: e.target.value})}
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setShowEditTaskModal(false); setEditingTask(null); }}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600"
                  onClick={handleUpdateTask}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Update Task
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task Detail Modal with Comments */}
      {showTaskDetailModal && selectedTaskForDetail && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 rounded-t-xl flex items-center justify-between">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                Task Details & Communication
              </h2>
              <button 
                onClick={() => { setShowTaskDetailModal(false); setSelectedTaskForDetail(null); setTaskComments([]); }}
                className="p-2 hover:bg-white/20 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Task Info */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{selectedTaskForDetail.title}</h3>
                  <p className="text-sm text-gray-500">Assigned to: {selectedTaskForDetail.assigned_to_name}</p>
                </div>
                <div className="flex gap-2">
                  <Badge className={`${
                    selectedTaskForDetail.status === 'completed' ? 'bg-green-100 text-green-700' :
                    selectedTaskForDetail.status === 'verified' ? 'bg-purple-100 text-purple-700' :
                    selectedTaskForDetail.status === 'in_progress' ? 'bg-orange-100 text-orange-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {selectedTaskForDetail.status}
                  </Badge>
                  <Badge variant="outline">{selectedTaskForDetail.priority}</Badge>
                </div>
              </div>
              
              <p className="text-gray-600">{selectedTaskForDetail.description}</p>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Due Date</p>
                  <p className="text-sm font-medium">{new Date(selectedTaskForDetail.due_date).toLocaleDateString()}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Created</p>
                  <p className="text-sm font-medium">{new Date(selectedTaskForDetail.created_at).toLocaleDateString()}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Priority</p>
                  <p className="text-sm font-medium capitalize">{selectedTaskForDetail.priority}</p>
                </div>
              </div>
              
              {/* Customer Contact Section */}
              {selectedTaskForDetail.customer_contact && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-600" />
                      <p className="font-semibold text-blue-800">Customer Contact</p>
                    </div>
                    {selectedTaskForDetail.customer_contact.phone && (
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => window.open(`tel:${selectedTaskForDetail.customer_contact.phone}`, '_self')}
                      >
                        <Phone className="w-4 h-4 mr-1" />
                        Call
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {selectedTaskForDetail.customer_contact.name && (
                      <div>
                        <p className="text-xs text-gray-500">Name</p>
                        <p className="font-medium">{selectedTaskForDetail.customer_contact.name}</p>
                      </div>
                    )}
                    {selectedTaskForDetail.customer_contact.phone && (
                      <div>
                        <p className="text-xs text-gray-500">Phone</p>
                        <p className="font-medium">{selectedTaskForDetail.customer_contact.phone}</p>
                      </div>
                    )}
                  </div>
                  
                  {selectedTaskForDetail.customer_contact.address && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500">Address</p>
                      <p className="text-sm">{selectedTaskForDetail.customer_contact.address}</p>
                      {selectedTaskForDetail.customer_contact.landmark && (
                        <p className="text-xs text-gray-500">Landmark: {selectedTaskForDetail.customer_contact.landmark}</p>
                      )}
                    </div>
                  )}
                  
                  {selectedTaskForDetail.customer_contact.latitude && selectedTaskForDetail.customer_contact.longitude && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2"
                      onClick={() => {
                        const url = `https://www.google.com/maps?q=${selectedTaskForDetail.customer_contact.latitude},${selectedTaskForDetail.customer_contact.longitude}`;
                        window.open(url, '_blank');
                      }}
                    >
                      <MapPin className="w-4 h-4 mr-1" />
                      View on Map
                    </Button>
                  )}
                </div>
              )}
              
              {/* Staff Communication Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                    <MessageCircle className="h-5 w-5 text-indigo-600" />
                    Staff Communication
                  </h4>
                  {taskComments.filter(c => c.sender_role === 'staff' && !c.read_by_admin).length > 0 && (
                    <Badge className="bg-red-500 text-white">
                      {taskComments.filter(c => c.sender_role === 'staff' && !c.read_by_admin).length} unread
                    </Badge>
                  )}
                </div>
                
                {/* Comments List */}
                <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto space-y-3 mb-4">
                  {loadingComments ? (
                    <div className="text-center py-8">
                      <Activity className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                      <p className="text-sm text-gray-500 mt-2">Loading messages...</p>
                    </div>
                  ) : taskComments.length === 0 ? (
                    <div className="text-center py-8">
                      <MessageCircle className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                      <p className="text-sm text-gray-500">No messages yet.</p>
                      <p className="text-xs text-gray-400">Start a conversation with the staff member.</p>
                    </div>
                  ) : (
                    taskComments.map((comment, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg max-w-[80%] ${
                          comment.sender_role === 'staff' 
                            ? 'bg-white border border-gray-200' 
                            : 'bg-indigo-100 ml-auto'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-medium ${
                            comment.sender_role === 'staff' ? 'text-blue-600' : 'text-indigo-600'
                          }`}>
                            {comment.sender_role === 'staff' ? `📱 ${comment.sender_name}` : `🔷 ${comment.sender_name} (Admin)`}
                          </span>
                          <span className="text-xs text-gray-400">
                            {new Date(comment.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-800">{comment.message}</p>
                      </div>
                    ))
                  )}
                </div>

                {/* Reply Input */}
                <div className="flex gap-2">
                  <Input
                    value={newAdminComment}
                    onChange={(e) => setNewAdminComment(e.target.value)}
                    placeholder="Type your reply to staff..."
                    className="flex-1"
                    onKeyPress={(e) => e.key === 'Enter' && handleSendAdminComment()}
                  />
                  <Button
                    onClick={handleSendAdminComment}
                    disabled={!newAdminComment.trim() || sendingComment}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {sendingComment ? (
                      <Activity className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Quick Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowTaskDetailModal(false);
                    handleEditTask(selectedTaskForDetail);
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Task
                </Button>
                {selectedTaskForDetail.status === 'completed' && (
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => setShowVerifyModal(true)}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Verify Task
                  </Button>
                )}
                {selectedTaskForDetail.status !== 'completed' && (
                  <Button
                    className="flex-1 bg-gray-400 cursor-not-allowed"
                    disabled
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {selectedTaskForDetail.status === 'verified' ? 'Already Verified' : 'Awaiting Completion'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Verify Task Modal */}
      {showVerifyModal && selectedTaskForDetail && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-4 rounded-t-xl flex items-center justify-between">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Verify Task Completion
              </h2>
              <button 
                onClick={() => setShowVerifyModal(false)}
                className="p-2 hover:bg-white/20 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-500">Task</p>
                <p className="font-medium">{selectedTaskForDetail.title}</p>
                <p className="text-sm text-gray-600">Assigned to: {selectedTaskForDetail.assigned_to_name}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount (₹)</label>
                <Input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="Enter payment amount to collect"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Verification Notes</label>
                <textarea
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  placeholder="Add notes for staff..."
                  className="w-full border rounded-lg p-2 min-h-[80px]"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowVerifyModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={handleVerifyTask}
                  disabled={verifyingTask}
                >
                  {verifyingTask ? (
                    <Activity className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Verify & Notify Staff
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && selectedUserForPassword && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 rounded-t-xl flex items-center justify-between">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Key className="h-5 w-5" />
                Change Password
              </h2>
              <button 
                onClick={() => setShowPasswordModal(false)}
                className="p-2 hover:bg-white/20 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-500">{selectedUserForPassword.userType === 'admin' ? 'Admin' : 'Staff'}</p>
                <p className="font-medium">{selectedUserForPassword.name}</p>
                <p className="text-sm text-gray-600">{selectedUserForPassword.email}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowPasswordModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  onClick={handleChangePassword}
                  disabled={changingPassword}
                >
                  {changingPassword ? (
                    <Activity className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Lock className="h-4 w-4 mr-2" />
                  )}
                  Change Password
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImprovedAdminDashboard;
